import PropTypes from "prop-types";
import { AlertTriangle, Info, Inbox } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  EmptyStateActionButtons,
  emptyStateTextClasses,
  resolveEmptyIcon,
} from "@/components/inbox/InboxChannelEmptyStateParts";

const VARIANT_STYLES = {
  action_required: {
    icon: AlertTriangle,
    iconWrap: "bg-amber-50 ring-amber-200/80",
    iconColor: "text-amber-600",
  },
  info: {
    icon: Info,
    iconWrap: "bg-slate-100 ring-slate-200",
    iconColor: "text-slate-500",
  },
  default: {
    icon: Inbox,
    iconWrap: "bg-indigo-50 ring-indigo-100",
    iconColor: "text-indigo-600",
  },
};

/**
 * Compact empty / blocked state for inbox channel tabs (sidebar + detail pane).
 */
export function InboxChannelEmptyState({
  variant = "default",
  title,
  description,
  actionUrl,
  actionLabel,
  settingsPath,
  ChannelIcon,
  compact = true,
  className,
}) {
  const style = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const Icon = resolveEmptyIcon(variant, ChannelIcon, style.icon);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "px-4 py-8" : "px-8 py-16",
        className,
      )}
      data-testid="inbox-channel-empty"
      data-variant={variant}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-xl ring-1",
          compact ? "h-10 w-10 mb-3" : "h-14 w-14 mb-4",
          style.iconWrap,
        )}
      >
        <Icon size={compact ? 18 : 24} className={style.iconColor} strokeWidth={1.75} />
      </div>
      <h3 className={cn("font-semibold text-slate-900 leading-snug", emptyStateTextClasses(compact, "title"))}>
        {title}
      </h3>
      {description ? (
        <p className={cn("mt-2 text-slate-500 leading-relaxed", emptyStateTextClasses(compact, "description"))}>
          {description}
        </p>
      ) : null}
      <EmptyStateActionButtons
        actionUrl={actionUrl}
        actionLabel={actionLabel}
        settingsPath={settingsPath}
        variant={variant}
        compact={compact}
      />
    </div>
  );
}

InboxChannelEmptyState.propTypes = {
  variant: PropTypes.oneOf(["action_required", "info", "default"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actionUrl: PropTypes.string,
  actionLabel: PropTypes.string,
  settingsPath: PropTypes.string,
  ChannelIcon: PropTypes.elementType,
  compact: PropTypes.bool,
  className: PropTypes.string,
};
